import type { Skill, SkillDetail } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { CodeBlock } from '@/components/code-block';
import { Field } from '@/components/field';
import { ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { formatBytes } from '@/lib/format';
import { useApiMutation, useSkills } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

const NAME_HINT = t('Lowercase letters, digits and hyphens, e.g. customer-replies.');

/** Same name rule as the Agent Skills format: claude.ai and ChatGPT accept these names too. */
export function skillNameError(name: string): string | null {
  if (!name) return null;
  return /^[a-z0-9]+(-[a-z0-9]+)*$/.test(name) && name.length <= 64 ? null : NAME_HINT;
}

/** Saves the skill zip (fetched with the Authorization header). */
export async function downloadSkill(workspaceId: string, skill: Pick<Skill, 'id' | 'name'>) {
  const { blob, filename } = await api.skills.export(workspaceId, skill.id);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `${skill.name}.zip`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

/** Create (no `skill`) or edit a workspace skill: name, description and instructions (SKILL.md). */
export function SkillDialog({ workspaceId, skill, children }: { workspaceId: string; skill?: SkillDetail; children: ReactNode }) {
  const navigate = useNavigate();
  const deskworksSkills = useSkills(workspaceId);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(skill?.name ?? '');
    setDescription(skill?.description ?? '');
    setBody(skill?.body ?? '');
  }, [open, skill]);

  const nameError = skillNameError(name.trim());
  const replaces = deskworksSkills.data?.find((s) => s.source === 'deskworks' && s.name === name.trim());

  const save = useApiMutation(
    () => {
      const input = { name: name.trim(), description: description.trim(), body };
      return skill ? api.skills.update(workspaceId, skill.id, input) : api.skills.create(workspaceId, input);
    },
    {
      success: skill ? t('Skill saved') : t('Skill created'),
      invalidate: (s) => [qk.skills(workspaceId), qk.skill(s.id), qk.agentAll],
      onSuccess: (s) => {
        setOpen(false);
        if (!skill) void navigate({ to: '/w/$workspaceId/skills/$skillId', params: { workspaceId, skillId: s.id } });
      },
    },
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() && description.trim() && !nameError) save.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{skill ? t('Edit skill') : t('New skill')}</DialogTitle>
            <DialogDescription>
              {t('A skill teaches agents how to do one kind of task. Agents always see its name and description, and load the instructions only when a task needs them.')}
            </DialogDescription>
          </DialogHeader>
          <Field
            label={t('Name')}
            error={nameError}
            hint={replaces ? t('A Deskworks skill has this name. In this workspace, agents will use your skill instead.') : NAME_HINT}
          >
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('e.g. customer-replies')} className="font-mono" autoFocus required />
          </Field>
          <Field label={t('Description')} hint={t('What the skill does and when an agent should use it. Agents decide by this text whether to load the skill.')}>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={1024} required />
          </Field>
          <Field label={t('Instructions')} hint={t('Markdown, like the body of a SKILL.md. Link extra files of the skill, e.g. references/examples.md.')}>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={16} className="font-mono text-[13px] leading-relaxed" />
          </Field>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!name.trim() || !description.trim() || !!nameError}>
              {skill ? t('Save') : t('Create skill')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Upload a skill exported from Claude, ChatGPT or Deskworks (.zip / .skill) or a single SKILL.md. */
export function ImportSkillDialog({ workspaceId, children }: { workspaceId: string; children: ReactNode }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [replace, setReplace] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setReplace(false);
  }, [open]);

  const upload = useApiMutation(
    async () => api.skills.import(workspaceId, { filename: file!.name, contentBase64: await fileToBase64(file!), replace }),
    {
      success: (s) => t('Skill “{name}” imported', { name: s.name }),
      errorTitle: t('Import failed'),
      invalidate: (s) => [qk.skills(workspaceId), qk.skill(s.id), qk.agentAll],
      onSuccess: (s) => {
        setOpen(false);
        void navigate({ to: '/w/$workspaceId/skills/$skillId', params: { workspaceId, skillId: s.id } });
      },
    },
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (file) upload.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('Import skill')}</DialogTitle>
            <DialogDescription>
              {t('Upload a skill folder as .zip (as exported from Claude, ChatGPT or Deskworks) or a single SKILL.md file.')}
            </DialogDescription>
          </DialogHeader>
          <Field label={t('File')} hint={t('Up to 5 MB. The archive must contain one SKILL.md.')}>
            <Input type="file" accept=".zip,.skill,.md" onChange={(e) => setFile(e.target.files?.[0] ?? null)} required />
          </Field>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={replace} onChange={(e) => setReplace(e.target.checked)} className="mt-0.5" />
            <span>
              {t('Replace my skill with the same name')}
              <span className="block text-xs text-muted-foreground">{t('Otherwise the import stops if this workspace already has a skill with that name.')}</span>
            </span>
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={upload.isPending} disabled={!file}>
              {t('Import')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Shows one file of a skill. For workspace skills it is editable; without
 * `path` it creates a new file.
 */
export function SkillFileDialog({
  workspaceId,
  skill,
  path,
  children,
}: {
  workspaceId: string;
  skill: SkillDetail;
  path?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [newPath, setNewPath] = useState('references/');
  const [content, setContent] = useState('');
  const editable = skill.source === 'workspace';
  const info = skill.files.find((f) => f.path === path);
  const binary = info?.encoding === 'base64';
  const file = useQuery({
    queryKey: qk.skillFile(skill.id, path ?? ''),
    queryFn: () => api.skills.file(workspaceId, skill.id, path!),
    enabled: open && !!path && !binary,
  });

  useEffect(() => {
    if (!open) return;
    setNewPath('references/');
    setContent(path ? (file.data?.content ?? '') : '');
  }, [open, path, file.data]);

  const save = useApiMutation(() => api.skills.putFile(workspaceId, skill.id, { path: path ?? newPath.trim(), content }), {
    success: path ? t('File saved') : t('File added'),
    invalidate: [qk.skill(skill.id), qk.skills(workspaceId)],
    onSuccess: () => setOpen(false),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (editable && !binary) save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle className="font-mono text-base">{path ?? t('New file')}</DialogTitle>
            <DialogDescription>
              {path
                ? info &&
                  (binary
                    ? t('{size} · binary file. Agents read it when the skill’s instructions point to it.', { size: formatBytes(info.size) })
                    : t('{size}. Agents read it when the skill’s instructions point to it.', { size: formatBytes(info.size) }))
                : t('Extra material the instructions can point to, e.g. examples, templates or reference data.')}
            </DialogDescription>
          </DialogHeader>
          {!path && (
            <Field label={t('Path')} hint={t('Inside the skill folder, e.g. references/examples.md or assets/template.md.')}>
              <Input value={newPath} onChange={(e) => setNewPath(e.target.value)} className="font-mono" required />
            </Field>
          )}
          {binary ? (
            <p className="text-sm text-muted-foreground">{t('Binary files can’t be shown here. Export the skill to get it.')}</p>
          ) : path && file.isPending ? (
            <LoadingRows rows={4} />
          ) : path && file.isError ? (
            <ErrorState error={file.error} onRetry={() => void file.refetch()} />
          ) : editable ? (
            <Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={18} className="font-mono text-[13px] leading-relaxed" />
          ) : (
            <CodeBlock value={content} maxHeight="max-h-[60vh]" />
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {editable && !binary ? t('Cancel') : t('Close')}
            </Button>
            {editable && !binary && (
              <Button type="submit" loading={save.isPending} disabled={!path && !newPath.trim()}>
                {path ? t('Save') : t('Add file')}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Choose which skills an agent may use. Shows the version that applies in this workspace. */
export function AgentSkillsDialog({
  workspaceId,
  agentId,
  enabled,
  children,
}: {
  workspaceId: string;
  agentId: string;
  enabled: Skill[];
  children: ReactNode;
}) {
  const skills = useSkills(workspaceId);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) setSelected(new Set(enabled.map((s) => s.name)));
  }, [open, enabled]);

  const choices = (skills.data ?? []).filter((s) => !s.overriddenBy && s.status !== 'retired');
  const save = useApiMutation(() => api.skills.setForAgent(agentId, choices.filter((s) => selected.has(s.name)).map((s) => s.id)), {
    success: t('Skills saved'),
    invalidate: [qk.agent(agentId), qk.skills(workspaceId), ['skill']],
    onSuccess: () => setOpen(false),
  });

  const toggle = (name: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(name);
      else next.delete(name);
      return next;
    });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('Skills of this agent')}</DialogTitle>
          <DialogDescription>
            {t('The agent sees the descriptions of these skills and loads a skill’s instructions only when a task needs it.')}
          </DialogDescription>
        </DialogHeader>
        {skills.isPending ? (
          <LoadingRows rows={3} />
        ) : skills.isError ? (
          <ErrorState error={skills.error} onRetry={() => void skills.refetch()} />
        ) : choices.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('There are no skills yet. Create or import one under Skills.')}</p>
        ) : (
          <ul className="-mx-1 max-h-[55vh] divide-y overflow-y-auto">
            {choices.map((s) => (
              <li key={s.id}>
                <label className="flex cursor-pointer items-start gap-3 px-1 py-2.5">
                  <Checkbox checked={selected.has(s.name)} onChange={(e) => toggle(s.name, e.target.checked)} className="mt-0.5" />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="font-mono text-[13px] font-medium">{s.name}</span>
                      <SkillSourceBadge skill={s} />
                      {s.status === 'disabled' && <Badge tone="amber">{t('disabled')}</Badge>}
                    </span>
                    <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">{s.description}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => setOpen(false)}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!skills.data}>
            {t('Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SkillSourceBadge({ skill }: { skill: Pick<Skill, 'source' | 'overrides'> }) {
  if (skill.source === 'deskworks') return <Badge tone="accent">Deskworks</Badge>;
  return <Badge tone="outline">{skill.overrides ? t('Customized') : t('Workspace')}</Badge>;
}
